import { CHANGELOG, type ChangelogEntry } from "@/lib/changelog";

/**
 * "What's new" modal — pokazuje listę zmian od ostatnio widzianej wersji
 * do aktualnej po update aplikacji.
 *
 * Storage: localStorage per-device (`kd_last_seen_version`). Per-user przez
 * Firestore byłby cleaniejszy ale wymaga writeu i nie ma ROI — user widzi
 * modal raz na update, nie boli go że na drugim urządzeniu też zobaczy.
 *
 * Logika "co pokazać":
 *   - Last seen = current → brak modal.
 *   - Last seen istnieje w CHANGELOG → entries `slice(0, lastIdx)` (CHANGELOG
 *     jest posortowany DESC, najnowsze pierwsze).
 *   - Last seen nieistniejący w CHANGELOG → cap 5 ostatnich entries
 *     (user na bardzo starej wersji lub wpis usunięty).
 *   - Brak entry w localStorage + świeży signup (< 24h) → mark cicho,
 *     brak modal (welcome flow już ich wita).
 *   - Brak entry w localStorage + istniejący user (> 24h) → fallback
 *     ostatnie 3 entries. To dotyczy:
 *       a) WSZYSTKICH przy pierwszym deploy z modalem (nigdy wcześniej nie
 *          mieli lastSeen w LS) — inaczej modal nie pokazałby się NIGDY
 *          dla obecnych userów.
 *       b) User po czyszczeniu cache aplikacji / reinstall PWA.
 */
// v2 key bump (v1.40.1): pierwotna logika getUnseenEntries cicho ustawiała
// kd_last_seen_version dla każdego usera bez entry — co oznaczało że WSZYSCY
// istniejący userzy po deploy v1.40.0 dostali zapisane "1.40.0" w LS bez
// zobaczenia modal. Bez bump'a klucza nigdy by go nie zobaczyli aż do v1.41.
// Bump na _v2 resetuje stan dla wszystkich — przy następnym otwarciu modal
// się pokaże (logika fallback 3 entries dla istniejących userów >24h).
const LS_KEY = "kd_last_seen_version_v2";
const MAX_FALLBACK_ENTRIES = 5;
const FRESH_SIGNUP_WINDOW_MS = 24 * 60 * 60 * 1000;
const FIRST_TIME_ENTRIES = 3;

export function getLastSeenVersion(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}

export function markVersionSeen(version: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, version);
  } catch {
    /* private mode / storage full — silent */
  }
}

/**
 * Zwraca entries do pokazania w modalu. Empty array = nie pokazuj modal.
 * Caller decyduje czy mountować komponent na podstawie length.
 *
 * NIE markuje wersji jako widzianej — to robi dismiss handler w modalu.
 * (Inaczej user który zamknie aplikację przed kliknięciem "OK" już więcej
 * nie zobaczy listy zmian dla tej wersji.)
 *
 * Wyjątek: gdy brak `lastSeen` w localStorage I user to świeży signup
 * (account < 24h), markujemy cicho current i zwracamy []. Welcome flow ich
 * wita, modal byłby duplikacją. Bez `userCreatedAt` (np. AppShell mountowany
 * przed auth load) traktujemy jak istniejącego usera — fallback entries.
 */
export function getUnseenEntries(
  currentVersion: string,
  opts?: { userCreatedAt?: Date | null },
): ChangelogEntry[] {
  const lastSeen = getLastSeenVersion();
  if (lastSeen === currentVersion) return [];

  if (!lastSeen) {
    // Świeży signup (account < 24h) — silent mark, brak modal
    const createdAt = opts?.userCreatedAt;
    if (createdAt && Date.now() - createdAt.getTime() < FRESH_SIGNUP_WINDOW_MS) {
      markVersionSeen(currentVersion);
      return [];
    }
    // Istniejący user bez lastSeen (debiut feature lub reset cache) —
    // pokaż ostatnie N entries. Bez tego modal nigdy by się nie pojawił
    // dla obecnych userów (mechanizm dopiero co dodany).
    return CHANGELOG.slice(0, FIRST_TIME_ENTRIES);
  }

  const lastIdx = CHANGELOG.findIndex((e) => e.version === lastSeen);
  if (lastIdx === -1) {
    // last seen nieistniejący w CHANGELOG — fallback: ostatnie N entries
    return CHANGELOG.slice(0, MAX_FALLBACK_ENTRIES);
  }
  // CHANGELOG jest DESC — wszystkie newer niż lastSeen to slice(0, lastIdx)
  return CHANGELOG.slice(0, lastIdx);
}
